import React, { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { useTheme } from '../../contexts/ThemeContext';
import Sidebar from '../../components/layout/Sidebar';
import Header from '../../components/layout/Header';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { 
  Plus, FileText, Edit, Trash2, GripVertical, Eye, Copy,
  Type, ListChecks, Calendar, Upload, ToggleLeft, Hash,
  AlignLeft, ChevronDown, ChevronUp, Save, Loader2, X
} from 'lucide-react';
import { cn } from '../../lib/utils';
import { toast } from 'sonner';
import api from '../../lib/axios';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import { Switch } from '../../components/ui/switch';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../../components/ui/table';

const FIELD_TYPES = [
  { id: 'text', label: 'Texte court', icon: Type },
  { id: 'textarea', label: 'Texte long', icon: AlignLeft },
  { id: 'number', label: 'Nombre', icon: Hash },
  { id: 'select', label: 'Liste déroulante', icon: ListChecks },
  { id: 'checkbox', label: 'Case à cocher', icon: ToggleLeft },
  { id: 'date', label: 'Date', icon: Calendar },
  { id: 'file', label: 'Fichier', icon: Upload },
];

const PROJECT_TYPES = [
  { id: 'logo', label: 'Logo / Identité visuelle' },
  { id: 'website', label: 'Site web' },
  { id: 'mobile_app', label: 'Application mobile' },
  { id: 'video', label: 'Production vidéo' },
  { id: 'social_media', label: 'Réseaux sociaux' },
  { id: 'print', label: 'Print / Impression' },
  { id: 'other', label: 'Autre' },
];

const FormBuilderPage = () => {
  const { user } = useAuth();
  const { theme } = useTheme();
  const [collapsed, setCollapsed] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [forms, setForms] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showFormEditor, setShowFormEditor] = useState(false);
  const [editingForm, setEditingForm] = useState(null);
  const [saving, setSaving] = useState(false);
  const [showPreviewModal, setShowPreviewModal] = useState(false);
  const [previewForm, setPreviewForm] = useState(null);

  // Form editor state
  const [formData, setFormData] = useState({
    name: '',
    project_type: '',
    description: '',
    fields: [],
    is_active: true
  });

  // Preview handler
  const handlePreviewForm = (form) => {
    setPreviewForm(form);
    setShowPreviewModal(true);
  };

  // Preview current form being edited
  const handlePreviewCurrentForm = () => {
    if (formData.fields.length === 0) {
      toast.error('Ajoutez au moins un champ pour prévisualiser');
      return;
    }
    setPreviewForm({ ...formData, id: 'preview' });
    setShowPreviewModal(true);
  };

  useEffect(() => {
    fetchForms();
  }, []);

  const fetchForms = async () => {
    try {
      const res = await api.get('/forms/');
      setForms(res.data || []);
    } catch (error) {
      console.error('Error fetching forms:', error);
      // Demo data
      setForms([
        {
          id: 'form-1',
          name: 'Brief Logo',
          project_type: 'logo',
          description: 'Formulaire pour les demandes de création de logo',
          fields: [
            { id: 'f1', type: 'text', label: 'Nom de l\'entreprise', required: true },
            { id: 'f2', type: 'textarea', label: 'Décrivez votre activité', required: true },
            { id: 'f3', type: 'select', label: 'Style souhaité', options: ['Moderne', 'Classique', 'Minimaliste', 'Créatif'], required: true },
            { id: 'f4', type: 'file', label: 'Logos de référence', required: false },
          ],
          is_active: true,
          created_at: '2024-12-01'
        },
        {
          id: 'form-2',
          name: 'Brief Site Web',
          project_type: 'website',
          description: 'Formulaire pour les demandes de création de site web',
          fields: [
            { id: 'f1', type: 'text', label: 'URL actuelle (si existante)', required: false },
            { id: 'f2', type: 'select', label: 'Type de site', options: ['Vitrine', 'E-commerce', 'Blog', 'Application web'], required: true },
            { id: 'f3', type: 'number', label: 'Nombre de pages estimé', required: true },
            { id: 'f4', type: 'textarea', label: 'Fonctionnalités souhaitées', required: true },
          ],
          is_active: true,
          created_at: '2024-12-05'
        }
      ]);
    } finally {
      setLoading(false);
    }
  };

  const handleNewForm = () => {
    setEditingForm(null);
    setFormData({
      name: '',
      project_type: '',
      description: '',
      fields: [],
      is_active: true
    });
    setShowFormEditor(true);
  };

  const handleEditForm = (form) => {
    setEditingForm(form);
    setFormData({
      name: form.name,
      project_type: form.project_type,
      description: form.description || '',
      fields: form.fields || [],
      is_active: form.is_active !== false
    });
    setShowFormEditor(true);
  };

  const handleAddField = (type) => {
    const newField = {
      id: `field-${Date.now()}`,
      type: type,
      label: '',
      required: false,
      options: type === 'select' ? ['Option 1'] : undefined
    };
    setFormData(prev => ({
      ...prev,
      fields: [...prev.fields, newField]
    }));
  };

  const handleUpdateField = (fieldId, updates) => {
    setFormData(prev => ({
      ...prev,
      fields: prev.fields.map(f => f.id === fieldId ? { ...f, ...updates } : f)
    }));
  };

  const handleRemoveField = (fieldId) => {
    setFormData(prev => ({
      ...prev,
      fields: prev.fields.filter(f => f.id !== fieldId)
    }));
  };

  const handleMoveField = (index, direction) => {
    const newFields = [...formData.fields];
    const newIndex = direction === 'up' ? index - 1 : index + 1;
    if (newIndex < 0 || newIndex >= newFields.length) return;
    
    [newFields[index], newFields[newIndex]] = [newFields[newIndex], newFields[index]];
    setFormData(prev => ({ ...prev, fields: newFields }));
  };

  const handleSaveForm = async () => {
    if (!formData.name || !formData.project_type) {
      toast.error('Veuillez remplir le nom et le type de projet');
      return;
    }
    if (formData.fields.length === 0) {
      toast.error('Ajoutez au moins un champ au formulaire');
      return;
    }
    
    setSaving(true);
    try {
      if (editingForm) {
        await api.put(`/forms/${editingForm.id}`, formData);
        setForms(prev => prev.map(f => f.id === editingForm.id ? { ...f, ...formData } : f));
        toast.success('Formulaire mis à jour');
      } else {
        const res = await api.post('/forms/', formData);
        setForms(prev => [...prev, res.data || { ...formData, id: `form-${Date.now()}`, created_at: new Date().toISOString() }]);
        toast.success('Formulaire créé');
      }
      setShowFormEditor(false);
    } catch (error) {
      // Demo mode - just update local state
      if (editingForm) {
        setForms(prev => prev.map(f => f.id === editingForm.id ? { ...f, ...formData } : f));
      } else {
        setForms(prev => [...prev, { ...formData, id: `form-${Date.now()}`, created_at: new Date().toISOString() }]);
      }
      toast.success(editingForm ? 'Formulaire mis à jour' : 'Formulaire créé');
      setShowFormEditor(false);
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteForm = async (formId) => {
    if (!confirm('Supprimer ce formulaire ?')) return;
    
    try {
      await api.delete(`/forms/${formId}`);
    } catch (error) {
      // Continue anyway for demo
    }
    setForms(prev => prev.filter(f => f.id !== formId));
    toast.success('Formulaire supprimé');
  };

  const handleDuplicateForm = (form) => {
    const newForm = {
      ...form,
      id: `form-${Date.now()}`,
      name: `${form.name} (copie)`,
      created_at: new Date().toISOString()
    };
    setForms(prev => [...prev, newForm]);
    toast.success('Formulaire dupliqué');
  };

  const getProjectTypeLabel = (type) => {
    return PROJECT_TYPES.find(t => t.id === type)?.label || type;
  };

  const getFieldTypeIcon = (type) => {
    const fieldType = FIELD_TYPES.find(t => t.id === type);
    return fieldType ? fieldType.icon : Type;
  };

  return (
    <div className="min-h-screen bg-[#f8fafc]">
      <Sidebar 
        collapsed={collapsed} 
        setCollapsed={setCollapsed}
        mobileOpen={mobileOpen}
        setMobileOpen={setMobileOpen}
      />
      <Header collapsed={collapsed} setMobileOpen={setMobileOpen} />
      
      <main className={cn(
        "transition-all duration-300 p-6 md:p-8 pt-24",
        collapsed ? "md:ml-20" : "md:ml-64"
      )}>
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-6">
          <div>
            <h1 className="text-2xl md:text-3xl font-bold" style={{ color: theme.colors?.primary }}>
              Constructeur de formulaires
            </h1>
            <p className="text-[#64748b] mt-1">
              Créez des formulaires personnalisés pour chaque type de projet
            </p>
          </div>
          
          <Button 
            onClick={handleNewForm}
            className="rounded-full"
            style={{ backgroundColor: theme.colors?.accent }}
          >
            <Plus className="w-4 h-4 mr-2" />
            Nouveau formulaire
          </Button>
        </div>

        {/* Forms List */}
        <Card className="card">
          <CardContent className="p-0">
            {loading ? (
              <div className="flex items-center justify-center h-48">
                <Loader2 className="w-6 h-6 animate-spin" style={{ color: theme.colors?.primary }} />
              </div>
            ) : forms.length === 0 ? (
              <div className="text-center py-12 text-[#64748b]">
                <FileText className="w-10 h-10 mx-auto mb-2 opacity-30" />
                <p className="text-sm">Aucun formulaire créé</p>
                <Button 
                  variant="outline" 
                  className="mt-4"
                  onClick={handleNewForm}
                >
                  <Plus className="w-4 h-4 mr-2" />
                  Créer votre premier formulaire
                </Button>
              </div>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow className="bg-[#f8fafc]">
                    <TableHead className="font-medium text-xs">Nom</TableHead>
                    <TableHead className="font-medium text-xs">Type de projet</TableHead>
                    <TableHead className="font-medium text-xs">Champs</TableHead>
                    <TableHead className="font-medium text-xs">Statut</TableHead>
                    <TableHead className="font-medium text-xs text-center">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {forms.map((form) => (
                    <TableRow key={form.id} className="hover:bg-[#f8fafc]/50">
                      <TableCell>
                        <div>
                          <span className="font-medium" style={{ color: theme.colors?.primary }}>
                            {form.name}
                          </span>
                          {form.description && (
                            <p className="text-xs text-[#64748b] mt-0.5">{form.description}</p>
                          )}
                        </div>
                      </TableCell>
                      <TableCell>
                        <Badge variant="outline" className="text-xs">
                          {getProjectTypeLabel(form.project_type)}
                        </Badge>
                      </TableCell>
                      <TableCell className="text-sm text-[#64748b]">
                        {form.fields?.length || 0} champs
                      </TableCell>
                      <TableCell>
                        <Badge className={cn(
                          "text-[10px]",
                          form.is_active ? "bg-emerald-100 text-emerald-700" : "bg-slate-100 text-slate-700"
                        )}>
                          {form.is_active ? 'Actif' : 'Inactif'}
                        </Badge>
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center justify-center gap-1">
                          <Button 
                            variant="ghost" 
                            size="icon"
                            className="h-7 w-7 rounded-full"
                            onClick={() => handlePreviewForm(form)}
                            title="Aperçu"
                          >
                            <Eye className="w-3.5 h-3.5" />
                          </Button>
                          <Button 
                            variant="ghost" 
                            size="icon"
                            className="h-7 w-7 rounded-full"
                            onClick={() => handleEditForm(form)}
                            title="Modifier"
                          >
                            <Edit className="w-3.5 h-3.5" />
                          </Button>
                          <Button 
                            variant="ghost" 
                            size="icon"
                            className="h-7 w-7 rounded-full"
                            onClick={() => handleDuplicateForm(form)}
                            title="Dupliquer"
                          >
                            <Copy className="w-3.5 h-3.5" />
                          </Button>
                          <Button 
                            variant="ghost" 
                            size="icon"
                            className="h-7 w-7 rounded-full text-red-500"
                            onClick={() => handleDeleteForm(form.id)}
                            title="Supprimer"
                          >
                            <Trash2 className="w-3.5 h-3.5" />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </CardContent>
        </Card>

        {/* Form Editor Dialog */}
        <Dialog open={showFormEditor} onOpenChange={setShowFormEditor}>
          <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle style={{ color: theme.colors?.primary }}>
                {editingForm ? 'Modifier le formulaire' : 'Nouveau formulaire'}
              </DialogTitle>
            </DialogHeader>
            
            <div className="space-y-6 py-4">
              {/* Basic Info */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Nom du formulaire *</Label>
                  <Input
                    value={formData.name}
                    onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                    placeholder="Ex: Brief Logo"
                  />
                </div>
                
                <div className="space-y-2">
                  <Label>Type de projet *</Label>
                  <Select 
                    value={formData.project_type}
                    onValueChange={(v) => setFormData(prev => ({ ...prev, project_type: v }))}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Sélectionner..." />
                    </SelectTrigger>
                    <SelectContent>
                      {PROJECT_TYPES.map(type => (
                        <SelectItem key={type.id} value={type.id}>{type.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div className="space-y-2">
                <Label>Description</Label>
                <Textarea
                  value={formData.description}
                  onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
                  placeholder="Description du formulaire..."
                  rows={2}
                />
              </div>

              <div className="flex items-center gap-2">
                <Switch
                  checked={formData.is_active}
                  onCheckedChange={(v) => setFormData(prev => ({ ...prev, is_active: v }))}
                />
                <Label>Formulaire actif</Label>
              </div>

              {/* Fields */}
              <div className="border-t pt-4">
                <div className="flex items-center justify-between mb-3">
                  <Label className="text-base">Champs du formulaire</Label>
                </div>

                {/* Add field buttons */}
                <div className="flex flex-wrap gap-2 mb-4">
                  {FIELD_TYPES.map(fieldType => {
                    const Icon = fieldType.icon;
                    return (
                      <Button
                        key={fieldType.id}
                        variant="outline"
                        size="sm"
                        className="text-xs"
                        onClick={() => handleAddField(fieldType.id)}
                      >
                        <Icon className="w-3 h-3 mr-1" />
                        {fieldType.label}
                      </Button>
                    );
                  })}
                </div>

                {/* Fields list */}
                <div className="space-y-3">
                  {formData.fields.length === 0 ? (
                    <div className="text-center py-6 text-[#64748b] border-2 border-dashed rounded-lg">
                      <p className="text-sm">Cliquez sur un type de champ ci-dessus pour l'ajouter</p>
                    </div>
                  ) : (
                    formData.fields.map((field, index) => {
                      const FieldIcon = getFieldTypeIcon(field.type);
                      return (
                        <div key={field.id} className="flex items-start gap-2 p-3 bg-[#f8fafc] rounded-lg border">
                          <div className="flex flex-col gap-1 pt-2">
                            <Button
                              variant="ghost"
                              size="icon"
                              className="h-5 w-5"
                              onClick={() => handleMoveField(index, 'up')}
                              disabled={index === 0}
                            >
                              <ChevronUp className="w-3 h-3" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="icon"
                              className="h-5 w-5"
                              onClick={() => handleMoveField(index, 'down')}
                              disabled={index === formData.fields.length - 1}
                            >
                              <ChevronDown className="w-3 h-3" />
                            </Button>
                          </div>
                          
                          <div className="flex-1 space-y-2">
                            <div className="flex items-center gap-2">
                              <FieldIcon className="w-4 h-4 text-[#64748b]" />
                              <span className="text-xs text-[#64748b] uppercase">
                                {FIELD_TYPES.find(t => t.id === field.type)?.label}
                              </span>
                            </div>
                            
                            <Input
                              value={field.label}
                              onChange={(e) => handleUpdateField(field.id, { label: e.target.value })}
                              placeholder="Libellé du champ"
                              className="h-8 text-sm"
                            />
                            
                            {field.type === 'select' && (
                              <div className="space-y-1">
                                <Label className="text-xs">Options (une par ligne)</Label>
                                <Textarea
                                  value={field.options?.join('\n') || ''}
                                  onChange={(e) => handleUpdateField(field.id, { 
                                    options: e.target.value.split('\n').filter(o => o.trim()) 
                                  })}
                                  placeholder="Option 1&#10;Option 2&#10;Option 3"
                                  rows={3}
                                  className="text-sm"
                                />
                              </div>
                            )}
                            
                            <div className="flex items-center gap-2">
                              <Switch
                                checked={field.required}
                                onCheckedChange={(v) => handleUpdateField(field.id, { required: v })}
                              />
                              <Label className="text-xs">Obligatoire</Label>
                            </div>
                          </div>
                          
                          <Button
                            variant="ghost"
                            size="icon"
                            className="h-6 w-6 text-red-500"
                            onClick={() => handleRemoveField(field.id)}
                          >
                            <X className="w-3 h-3" />
                          </Button>
                        </div>
                      );
                    })
                  )}
                </div>
              </div>
            </div>

            <DialogFooter>
              <Button variant="outline" onClick={() => setShowFormEditor(false)}>
                Annuler
              </Button>
              <Button
                variant="outline"
                onClick={handlePreviewCurrentForm}
              >
                <Eye className="w-4 h-4 mr-2" />
                Aperçu
              </Button>
              <Button
                onClick={handleSaveForm}
                disabled={saving}
                style={{ backgroundColor: theme.colors?.accent }}
              >
                {saving ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Enregistrement...
                  </>
                ) : (
                  <>
                    <Save className="w-4 h-4 mr-2" />
                    Enregistrer
                  </>
                )}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Preview Modal */}
        <Dialog open={showPreviewModal} onOpenChange={setShowPreviewModal}>
          <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <Eye className="w-5 h-5" style={{ color: theme.colors?.accent }} />
                Aperçu du formulaire
              </DialogTitle>
            </DialogHeader>
            
            {previewForm && (
              <div className="space-y-6">
                {/* Form Header Preview */}
                <div className="p-4 bg-slate-50 rounded-xl border border-slate-200">
                  <h3 className="text-lg font-semibold" style={{ color: theme.colors?.primary }}>
                    {previewForm.name || 'Formulaire sans nom'}
                  </h3>
                  {previewForm.description && (
                    <p className="text-sm text-slate-500 mt-1">{previewForm.description}</p>
                  )}
                  <Badge variant="outline" className="mt-2">
                    {getProjectTypeLabel(previewForm.project_type)}
                  </Badge>
                </div>

                {/* Form Fields Preview */}
                <div className="space-y-4">
                  {previewForm.fields?.map((field, index) => {
                    const FieldIcon = getFieldTypeIcon(field.type);
                    return (
                      <div key={field.id || index} className="space-y-2">
                        <Label className="flex items-center gap-2">
                          <FieldIcon className="w-4 h-4 text-slate-400" />
                          {field.label || `Champ ${index + 1}`}
                          {field.required && <span className="text-red-500">*</span>}
                        </Label>
                        
                        {field.type === 'text' && (
                          <Input placeholder={`Entrez ${field.label?.toLowerCase() || 'une valeur'}...`} disabled className="bg-white" />
                        )}
                        
                        {field.type === 'textarea' && (
                          <Textarea placeholder={`Décrivez ${field.label?.toLowerCase() || '...'}...`} rows={3} disabled className="bg-white" />
                        )}
                        
                        {field.type === 'number' && (
                          <Input type="number" placeholder="0" disabled className="bg-white" />
                        )}
                        
                        {field.type === 'select' && (
                          <Select disabled>
                            <SelectTrigger className="bg-white">
                              <SelectValue placeholder="Sélectionner une option..." />
                            </SelectTrigger>
                            <SelectContent>
                              {field.options?.map((opt, i) => (
                                <SelectItem key={i} value={opt}>{opt}</SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        )}
                        
                        {field.type === 'checkbox' && (
                          <div className="flex items-center gap-2">
                            <Switch disabled />
                            <span className="text-sm text-slate-500">{field.label}</span>
                          </div>
                        )}
                        
                        {field.type === 'date' && (
                          <Input type="date" disabled className="bg-white" />
                        )}
                        
                        {field.type === 'file' && (
                          <div className="border-2 border-dashed border-slate-200 rounded-lg p-6 text-center">
                            <Upload className="w-8 h-8 text-slate-300 mx-auto mb-2" />
                            <p className="text-sm text-slate-500">Cliquez ou déposez un fichier ici</p>
                            <p className="text-xs text-slate-400 mt-1">PDF, PNG, JPG jusqu'à 10 Mo</p>
                          </div>
                        )}
                      </div>
                    );
                  })}

                  {(!previewForm.fields || previewForm.fields.length === 0) && (
                    <div className="text-center py-8 text-slate-500">
                      <FileText className="w-12 h-12 mx-auto mb-2 text-slate-300" />
                      <p>Aucun champ dans ce formulaire</p>
                    </div>
                  )}
                </div>

                {/* Submit Button Preview */}
                {previewForm.fields?.length > 0 && (
                  <div className="pt-4 border-t">
                    <Button 
                      className="w-full" 
                      style={{ backgroundColor: theme.colors?.accent }}
                      disabled
                    >
                      Soumettre la demande
                    </Button>
                    <p className="text-xs text-slate-400 text-center mt-2">
                      * Champs obligatoires
                    </p>
                  </div>
                )}
              </div>
            )}
            
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowPreviewModal(false)}>
                Fermer
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </main>
    </div>
  );
};

export default FormBuilderPage;
